import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { ThemeProvider } from 'styled-components';
import { Turnstile } from '@marsidev/react-turnstile';
import type { TurnstileInstance } from '@marsidev/react-turnstile';
import { darkTheme } from '@stylesheets/theme';
import useAuth from '@contexts/useAuth';
import { useSignupConfig } from '@hooks/useSignupConfig';
import LoginLayout from '@components/1-atoms/Layouts/LoginLayout/LoginLayout';
import PageHeader, {
  PAGE_HEADER_VARIANT_LARGE,
} from '@components/2-molecules/PageHeader';
import Field from '@components/1-atoms/Field';
import Button, { BUTTON_VARIANT_TEXT } from '@components/1-atoms/Button';
import EulaPdfModal from '@components/EulaPdfModal/EulaPdfModal';
import { STORAGE_KEYS, EULA_URL } from '@constants/index';
import { RETURN_TO_PARAM, rememberReturnTo } from '@utils/ssoSession';
import {
  ErrorBox,
  SuccessBox,
  Divider,
  CheckboxRow,
  InheritLink,
  LinkButton,
} from '../auth.styled';
import {
  Header,
  Logo,
  BrandName,
  LoginForm,
  LoginStepContent,
} from '../Login/Login.styled';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

const Signup: React.FC = () => {
  const { signup, isLoading, error, clearError, camLoginUrl } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { signupBlocked, requiredDomain } = useSignupConfig();
  const [step, setStep] = useState<'email' | 'password'>('email');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupDisplayName, setSignupDisplayName] = useState('');
  const [showEulaModal, setShowEulaModal] = useState(false);
  const [revealPassword, setRevealPassword] = useState(false);
  const [revealConfirmPassword, setRevealConfirmPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);

  useEffect(() => {
    rememberReturnTo(new URLSearchParams(location.search).get(RETURN_TO_PARAM));
  }, [location.search]);

  const emailSchema = Yup.object({
    email: Yup.string()
      .email('Invalid email')
      .required('Email is required')
      .test(
        'domain-check',
        requiredDomain
          ? `Email must end with @${requiredDomain}`
          : 'Invalid email',
        (value) => {
          if (!value || !requiredDomain) return true;
          return value.toLowerCase().endsWith(`@${requiredDomain.toLowerCase()}`);
        },
      ),
    displayName: Yup.string()
      .trim()
      .required('Name is required')
      .min(2, 'Must be at least 2 characters'),
  });

  const passwordSchema = Yup.object({
    password: Yup.string()
      .min(8, 'Must be at least 8 characters')
      .required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .required('Please confirm your password'),
    eulaAccepted: Yup.boolean()
      .oneOf([true], 'You must accept the End User License Agreement')
      .required('You must accept the End User License Agreement'),
  });

  const handleEmailSubmit = useCallback(
    (values: { email: string; displayName: string }) => {
      clearError();
      setSignupEmail(values.email);
      setSignupDisplayName(values.displayName);
      setStep('password');
    },
    [clearError],
  );

  const handlePasswordSubmit = useCallback(
    async (
      values: { password: string; confirmPassword: string },
      { setSubmitting }: { setSubmitting: (s: boolean) => void },
    ) => {
      try {
        const result = await signup(
          signupEmail,
          values.password,
          signupDisplayName || undefined,
          turnstileToken || undefined,
        );
        navigate('/auth/verify', {
          state: {
            email: signupEmail,
            codeResent: result.status === 'code_resent',
          },
        });
      } catch {
        // Reset Turnstile widget so user can retry
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        setSubmitting(false);
      }
    },
    [signup, signupEmail, signupDisplayName, navigate, turnstileToken],
  );

  const toggleRevealPassword = useCallback(() => setRevealPassword((p) => !p), []);
  const toggleRevealConfirmPassword = useCallback(
    () => setRevealConfirmPassword((p) => !p),
    [],
  );

  const handleOpenEula = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowEulaModal(true);
  }, []);

  // Invited users carry a pending invite token; signups are blocked globally but
  // the backend bypasses the block for invited emails (signupsDisabledFor), so
  // let them reach the form. Backend remains the source of truth on submit.
  const hasPendingInvite = !!localStorage.getItem(STORAGE_KEYS.PENDING_INVITE_TOKEN);
  if (signupBlocked && !hasPendingInvite) {
    return <Navigate to="/auth/login" replace />;
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <LoginLayout>
        <Header>
          <Logo />
          <BrandName>Aibuild OS</BrandName>
        </Header>

        {step === 'email' ? (
          <Formik
            key="email-step"
            initialValues={{ email: signupEmail, displayName: signupDisplayName }}
            validationSchema={emailSchema}
            onSubmit={handleEmailSubmit}
            enableReinitialize
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
              <LoginForm onSubmit={handleSubmit}>
                <LoginStepContent>
                  <PageHeader
                    variant={PAGE_HEADER_VARIANT_LARGE}
                    title="Create account"
                    subtitle="Get started with Aibuild OS."
                  />

                  {requiredDomain && (
                    <SuccessBox>
                      Use an <strong>@{requiredDomain}</strong> email address to sign up.
                    </SuccessBox>
                  )}

                  <Field
                    name="displayName"
                    type="text"
                    placeholder="Name"
                    value={values.displayName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!(touched.displayName && errors.displayName)}
                    supportingText={
                      touched.displayName && errors.displayName
                        ? errors.displayName
                        : undefined
                    }
                  />

                  <Field
                    name="email"
                    type="email"
                    placeholder={requiredDomain ? `you@${requiredDomain}` : 'Email address'}
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!(touched.email && errors.email)}
                    supportingText={
                      touched.email && errors.email ? errors.email : undefined
                    }
                  />

                  <Button type="submit">Continue</Button>

                  <Divider>OR</Divider>

                  <Button
                    type="button"
                    variant={BUTTON_VARIANT_TEXT}
                    onClick={() => navigate('/auth/login')}
                  >
                    Already have an account? Log in
                  </Button>
                </LoginStepContent>
              </LoginForm>
            )}
          </Formik>
        ) : (
          <Formik
            key="password-step"
            initialValues={{ password: '', confirmPassword: '', eulaAccepted: false }}
            validationSchema={passwordSchema}
            onSubmit={handlePasswordSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <LoginForm onSubmit={handleSubmit}>
                <LoginStepContent>
                  <PageHeader
                    variant={PAGE_HEADER_VARIANT_LARGE}
                    title="Create account"
                    subtitle="Choose a secure password."
                  />

                  <Field
                    name="password"
                    type={revealPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!(touched.password && errors.password)}
                    supportingText={
                      touched.password && errors.password
                        ? errors.password
                        : undefined
                    }
                    endingButtonIconName={
                      revealPassword ? 'visibility_on_1' : 'visibility_off_0'
                    }
                    onEndingButtonClick={toggleRevealPassword}
                  />

                  <Field
                    name="confirmPassword"
                    type={revealConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm password"
                    value={values.confirmPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!(touched.confirmPassword && errors.confirmPassword)}
                    supportingText={
                      touched.confirmPassword && errors.confirmPassword
                        ? errors.confirmPassword
                        : undefined
                    }
                    endingButtonIconName={
                      revealConfirmPassword ? 'visibility_on_1' : 'visibility_off_0'
                    }
                    onEndingButtonClick={toggleRevealConfirmPassword}
                  />

                  <CheckboxRow>
                    <input
                      type="checkbox"
                      name="eulaAccepted"
                      checked={values.eulaAccepted}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <span>
                      I have read and agree to the{' '}
                      <LinkButton type="button" onClick={handleOpenEula}>
                        End User License Agreement
                      </LinkButton>
                    </span>
                  </CheckboxRow>
                  {touched.eulaAccepted && errors.eulaAccepted && (
                    <ErrorBox>{errors.eulaAccepted}</ErrorBox>
                  )}

                  {TURNSTILE_SITE_KEY && (
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={TURNSTILE_SITE_KEY}
                      onSuccess={(token) => setTurnstileToken(token)}
                      onExpire={() => setTurnstileToken(null)}
                      onError={() => setTurnstileToken(null)}
                      options={{ theme: 'dark' }}
                    />
                  )}

                  {error && (
                    <ErrorBox>
                      {error}
                      {camLoginUrl && (
                        <>
                          {' '}
                          <InheritLink href={camLoginUrl}>
                            Go to Aibuild CAM login &rarr;
                          </InheritLink>
                        </>
                      )}
                    </ErrorBox>
                  )}

                  <Button
                    type="submit"
                    disabled={isSubmitting || isLoading || (!!TURNSTILE_SITE_KEY && !turnstileToken)}
                    loading={isLoading}
                  >
                    {isLoading ? 'Creating account...' : 'Create account'}
                  </Button>

                  <Button
                    type="button"
                    variant={BUTTON_VARIANT_TEXT}
                    onClick={() => setStep('email')}
                  >
                    Back
                  </Button>
                </LoginStepContent>
              </LoginForm>
            )}
          </Formik>
        )}

        {showEulaModal && (
          <EulaPdfModal url={EULA_URL} onClose={() => setShowEulaModal(false)} />
        )}
      </LoginLayout>
    </ThemeProvider>
  );
};

export default Signup;
